import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useApplicantAuth } from "@/context/ApplicantAuthContext";
import applicantApi from "@/lib/applicantApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ShieldCheck,
  LogOut,
  Play,
  Lock,
  CheckCircle2,
  Hourglass,
  FileCheck,
  CalendarCheck,
  ExternalLink,
} from "lucide-react";

const STATUS_UI = {
  submitted:         { label: "Application in progress", tone: "secondary" },
  in_review:         { label: "Submitted · Under review", tone: "secondary" },
  pre_screened:      { label: "Pre-screened · Training unlocked", tone: "default" },
  training_started:  { label: "Training in progress", tone: "default" },
  training_complete: { label: "Training complete", tone: "default" },
  second_interview:  { label: "Invited to 2nd interview", tone: "default" },
  hired:             { label: "Hired", tone: "default" },
  rejected:          { label: "Not moving forward", tone: "secondary" },
};

const ROLE_LABELS = {
  caregiver: "Caregiver",
  care_coordinator: "Care Coordinator",
  care_supervisor: "Care Supervisor",
  care_concierge: "Care Concierge",
  admin_hr: "Admin / HR",
};

const ApplicantDashboard = () => {
  const { applicant, checking, logout, refresh } = useApplicantAuth();
  const nav = useNavigate();
  const [modules, setModules] = useState([]);
  const [progressByModule, setProgressByModule] = useState({});
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState({ available: false, url: "" });

  useEffect(() => {
    if (checking) return;
    if (!applicant) { nav("/careers/login"); return; }
    (async () => {
      try {
        if (["pre_screened", "training_started", "training_complete", "second_interview", "hired"].includes(applicant.status)) {
          const { data } = await applicantApi.get("/applicants/me/training-modules");
          setModules(data);
          const map = {};
          for (const m of data) {
            try {
              const { data: p } = await applicantApi.get(`/applicants/me/training-progress/${m.id}`);
              map[m.id] = p;
            } catch { /* ignore */ }
          }
          setProgressByModule(map);
        }
        // Fetch scheduling info (available only for second_interview / hired)
        try {
          const { data: s } = await applicantApi.get("/applicants/me/scheduling");
          setScheduling(s);
        } catch { /* ignore */ }
      } finally {
        setLoading(false);
      }
    })();
  }, [applicant, checking, nav]);

  if (checking) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!applicant) return <Navigate to="/careers/login" replace />;

  const s = STATUS_UI[applicant.status] || STATUS_UI.submitted;
  const canTrain = ["pre_screened", "training_started", "training_complete", "second_interview", "hired"].includes(applicant.status);
  const roleLabel = ROLE_LABELS[applicant.applied_role || "caregiver"] || "Caregiver";

  return (
    <main data-testid="applicant-dashboard" className="min-h-screen bg-background">
      <div className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/careers" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-serif-display">C</span>
            <span className="font-serif-display text-lg">Care Anywhere · Careers</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-muted-foreground">{applicant.email}</span>
            <Button variant="ghost" size="sm" data-testid="btn-applicant-logout" onClick={() => { logout(); nav("/careers/login"); }} className="rounded-full">
              <LogOut size={14} className="mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Applicant portal</div>
        <h1 className="mt-3 font-serif-display text-3xl md:text-4xl">Welcome back, {applicant.first_name}.</h1>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Badge data-testid="applicant-status-badge" variant={s.tone}>{s.label}</Badge>
          <Badge variant="secondary" data-testid="applicant-role-badge">Applied for: {roleLabel}</Badge>
          {applicant.submitted_at && (
            <span className="text-xs text-muted-foreground">Submitted {new Date(applicant.submitted_at).toLocaleDateString()}</span>
          )}
        </div>

        {/* Second interview scheduling banner */}
        {scheduling.available && (
          <div data-testid="scheduling-banner" className="mt-8 rounded-2xl border border-primary/40 bg-primary/5 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <CalendarCheck size={22} className="text-primary mt-0.5" />
              <div>
                <div className="font-serif-display text-xl">You're invited to book your 2nd interview.</div>
                <div className="text-sm text-muted-foreground mt-1">Pick a time that works for you. Interviews are ~30 minutes and can be virtual or in-person.</div>
              </div>
            </div>
            {scheduling.url ? (
              <a
                data-testid="btn-book-interview"
                href={scheduling.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full h-11 px-6 bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
              >
                Book my interview <ExternalLink size={13} />
              </a>
            ) : (
              <span className="text-xs text-muted-foreground">The scheduling link is being finalized — you'll receive an email shortly.</span>
            )}
          </div>
        )}

        {/* Stage 1 status card */}
        <section className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-border/70 bg-card p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FileCheck size={18} className="text-primary" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Stage 1</div>
                <div className="font-serif-display text-xl">Vetting application</div>
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              {applicant.status === "submitted"
                ? "You have a saved draft. Complete every section to submit for review."
                : "Received — our HR team will contact your references and confirm your credentials."}
            </p>
            {applicant.status === "submitted" && (
              <Button asChild data-testid="btn-resume-application" className="mt-5 rounded-full bg-primary text-primary-foreground">
                <Link to="/careers/apply">Resume application</Link>
              </Button>
            )}
          </div>

          <div className="rounded-2xl border border-border/70 bg-card p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                {canTrain ? <ShieldCheck size={18} className="text-primary" /> : <Lock size={18} className="text-muted-foreground" />}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Stage 2</div>
                <div className="font-serif-display text-xl">Training modules</div>
              </div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              {canTrain
                ? "Unlocked. Complete every module to proceed to interview scheduling."
                : "Locked until our HR team pre-screens your application (typically within 3 business days)."}
            </p>
          </div>
        </section>

        {/* Training list */}
        <section className="mt-12">
          <h2 className="font-serif-display text-2xl">Training modules</h2>
          <p className="text-sm text-muted-foreground mt-1">Videos are un-skippable and pause automatically if you switch tabs. Progress is saved every few seconds.</p>
          {!canTrain && (
            <div data-testid="training-locked-card" className="mt-6 rounded-2xl border border-border/70 bg-muted/40 p-6 flex items-start gap-4">
              <Lock size={22} className="text-muted-foreground mt-1" />
              <div className="text-sm text-muted-foreground">Training will appear here as soon as an HR reviewer approves your application.</div>
            </div>
          )}
          {canTrain && loading && <div className="mt-6 text-sm text-muted-foreground">Loading modules…</div>}
          {canTrain && !loading && (
            <div className="mt-6 space-y-4" data-testid="training-modules-list">
              {modules.map((m) => {
                const prog = progressByModule[m.id] || { max_watched_seconds: 0, completed_at: null, passed: false, score_pct: 0 };
                const pct = m.duration_seconds ? Math.min(100, Math.round((prog.max_watched_seconds / m.duration_seconds) * 100)) : 0;
                const passed = !!prog.passed;
                const attempted = (prog.score_pct || 0) > 0 || (prog.assessment_answers && prog.assessment_answers.length > 0);
                return (
                  <div key={m.id} data-testid={`module-row-${m.slug}`} className="rounded-2xl border border-border/70 bg-card p-6 flex flex-col md:flex-row md:items-center gap-6">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-serif-display text-xl text-foreground">{m.title}</div>
                        {passed && <Badge className="rounded-full">Passed · {prog.score_pct}%</Badge>}
                        {!passed && attempted && <Badge variant="secondary" className="rounded-full">Attempted · {prog.score_pct}%</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {m.category?.replace(/_/g, " ")} · ~{Math.round((m.duration_seconds || 0) / 60)} min video · {m.final_questions?.length || 0} assessment questions · {m.passing_score || 80}% to pass
                      </div>
                      <div className="mt-4">
                        <Progress data-testid={`module-progress-${m.slug}`} value={pct} className="h-2" />
                        <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2">
                          {passed ? <><CheckCircle2 size={12} className="text-primary" /> Completed &amp; passed</> : <><Hourglass size={12} /> {pct}% watched</>}
                        </div>
                      </div>
                    </div>
                    <Button asChild data-testid={`btn-start-${m.slug}`} className="rounded-full bg-primary text-primary-foreground h-11 px-6">
                      <Link to={`/careers/training/${m.id}`}>
                        <Play size={14} className="mr-1.5" /> {prog.max_watched_seconds > 0 ? (passed ? "Review" : "Continue") : "Start"}
                      </Link>
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default ApplicantDashboard;
